import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MOTORCYCLES, SCOOTERS, VIDA } from "../../data/site";
import { fetchPrices } from "../../lib/api";
import { Reveal, Label } from "./Reveal";
import { ArrowUpRight } from "lucide-react";

const TABS = [
  { id: "motorcycles", label: "Motorcycles", data: MOTORCYCLES },
  { id: "scooters", label: "Scooters", data: SCOOTERS },
  { id: "vida", label: "VIDA Electric", data: VIDA },
];

const inr = (n) => "₹ " + Number(n).toLocaleString("en-IN");

const Card = ({ v, i, onEnquire, price }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: i * 0.05 }}
    className="group relative border border-white/10 bg-[#111] hover:border-red-600/60 transition-colors duration-300"
    data-testid={`vehicle-card-${v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
  >
    <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-zinc-100 to-zinc-300 p-4">
      {v.tag && (
        <span className="absolute top-3 left-3 z-10 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">
          {v.tag}
        </span>
      )}
      <img
        src={v.img}
        alt={v.name}
        loading="lazy"
        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
    </div>
    <div className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg md:text-xl font-semibold uppercase text-white leading-tight">{v.name}</h3>
          <p className="text-sm text-zinc-400 mt-1">{v.cc} • {v.spec}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
        <span className="text-red-500 font-bold" data-testid={`price-${v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {price != null ? (
            <><span className="block text-[10px] text-zinc-500 font-semibold uppercase tracking-widest">On-road from</span>{inr(price)}</>
          ) : "Price on request"}
        </span>
        <button
          onClick={() => onEnquire(v.name)}
          data-testid={`enquire-${v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-white group/btn"
        >
          Enquire
          <ArrowUpRight size={16} className="text-red-500 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
        </button>
      </div>
    </div>
  </motion.div>
);

export const VehicleShowcase = ({ onEnquire }) => {
  const [tab, setTab] = useState("motorcycles");
  const [prices, setPrices] = useState({});
  const active = TABS.find((t) => t.id === tab);

  useEffect(() => {
    fetchPrices()
      .then((r) => {
        const map = {};
        (r.data || []).forEach((p) => { map[p.name] = p.price; });
        setPrices(map);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="vehicles" className="relative py-24 md:py-32 bg-[#0a0a0a]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal>
          <Label>The Line-up</Label>
          <h2 className="mt-4 font-heading text-4xl sm:text-6xl font-bold uppercase tracking-tighter text-white">
            Find your <span className="text-red-600">machine</span>
          </h2>
          <p className="mt-4 max-w-2xl text-zinc-400">
            Every Hero scooter & motorcycle and the full VIDA electric range — all available at Sasya Hero.
            Prices shown are on-road starting prices; talk to us for the best deal & finance.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-2 border-b border-white/10">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`relative px-5 py-3 font-heading text-sm md:text-base font-semibold uppercase tracking-wide transition-colors ${
                tab === t.id ? "text-white" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {t.label}
              {tab === t.id && <motion.span layoutId="tab-underline" className="absolute -bottom-px left-0 right-0 h-0.5 bg-red-600" />}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {active.data.map((v, i) => (
              <Card key={v.name + i} v={v} i={i} onEnquire={onEnquire} price={prices[v.name]} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
